/**
 * A mermaid diagram, drawn inside the editor (TASK-050 phase 2).
 *
 * Euan: "We can get rid of gap comments, but we need to do the node views for
 * diagrams." Without this, a document with a diagram in it became a document
 * with a wall of `graph TD` in it the moment somebody could edit -- which is
 * a worse document than the one they started with.
 *
 * ==================================================================
 * TWO FACES, ONE NODE
 * ==================================================================
 * A diagram is a `codeBlock` whose language is `mermaid`: the source IS the
 * document's content, and the picture is a rendering of it. So the node view
 * keeps both and shows one:
 *
 *   * not selected -> the picture, and the source is hidden;
 *   * selected, or the caret inside it -> the source, editable, because that
 *     is the only thing there is to edit.
 *
 * `contentDOM` is the hidden `<pre>` either way. ProseMirror needs a real,
 * attached content host to put a selection in; handing it one that only
 * exists while selected would mean the caret had nowhere to land in order to
 * become selected in the first place.
 *
 * ==================================================================
 * WHY MERMAID IS ALLOWED TO OWN ITS SUBTREE
 * ==================================================================
 * `mermaid.run()` replaces the element's contents wholesale and stamps
 * `data-processed` on what it has handled. ProseMirror is told to keep out via
 * `ignoreMutation`, exactly as the read view keeps Svelte out by giving
 * mermaid an element with no template children: two things diffing one
 * subtree is how a diagram ends up half-drawn.
 */
import type { Node as PMNode } from 'prosemirror-model'
import { TextSelection } from 'prosemirror-state'
import type { EditorView, NodeView, ViewMutationRecord } from 'prosemirror-view'
import { loadMermaid } from '../mermaid'
import { coverOfThreads, type BlockRange } from '../anchors'
import { LABEL_SELECTOR, diagramKey, diagramShown, labelKey } from '../diagram'
import type { Highlights } from '../highlights.svelte'

export class DiagramView implements NodeView {
  dom: HTMLElement
  contentDOM: HTMLElement

  #figure: HTMLElement
  #source: HTMLElement
  #node: PMNode
  #view: EditorView
  #getPos: () => number | undefined
  #drawn: string | null = null
  #selected = false
  /** The comment ranges on this diagram's source, newest wins. */
  #ranges: BlockRange[] = []
  #active: string | null = null
  #hl: Highlights | null = null
  /** Which threads the pins were last drawn for; see #drawPins. */
  #pinSig = ''

  constructor(node: PMNode, view: EditorView, getPos: () => number | undefined) {
    this.#node = node
    this.#view = view
    this.#getPos = getPos

    /*
     * The SAME shape the read view gives a diagram: `.diagram[data-id]` around
     * `.mermaid[data-diagram]`. Comment anchoring finds a diagram by walking
     * up to `[data-diagram]` and reads its labels from inside, and the
     * highlight machinery tints them through the same classes -- so matching
     * the read view's contract means one implementation serves both, rather
     * than a second one that has to be kept in step.
     */
    this.dom = document.createElement('div')
    this.dom.className = 'diagram pm-diagram'
    this.dom.setAttribute('data-id', String(node.attrs.id ?? ''))

    // The picture. Mermaid owns everything inside it.
    this.#figure = document.createElement('div')
    this.#figure.className = 'pm-diagram-figure mermaid'
    this.#figure.setAttribute('data-diagram', String(node.attrs.id ?? ''))
    /*
     * DOUBLE click opens the source, not a single one.
     *
     * A single click with `preventDefault` was the first version, and it made
     * the diagram impossible to SELECT text in -- which is how a person
     * comments on a label (TASK-045). Commenting on a diagram is the more
     * common thing to want and the one that was already there, so it keeps
     * the plain gesture; editing the source, which is rarer and irreversible
     * in the sense that it changes the picture, takes the deliberate one.
     *
     * That was BUG-028: the node view rendered the picture and quietly took
     * away the ability to talk about it.
     */
    this.#figure.addEventListener('dblclick', (event) => {
      event.preventDefault()
      const pos = this.#getPos()
      if (pos === undefined) return
      const inside = TextSelection.near(this.#view.state.doc.resolve(pos + 1))
      this.#view.dispatch(this.#view.state.tr.setSelection(inside))
      this.#view.focus()
    })

    // The source, which is the document's actual content.
    this.#source = document.createElement('pre')
    this.#source.className = 'pm-diagram-source'
    const code = document.createElement('code')
    this.#source.appendChild(code)
    this.contentDOM = code

    /*
     * The pins: one button per thread on this diagram, exactly as the read
     * view renders them. They are how a comment on a picture is reachable at
     * all by keyboard -- an SVG label is not focusable, and a thread nobody
     * can get to is a thread nobody answers.
     */
    this.dom.appendChild(this.#figure)
    this.dom.appendChild(this.#source)
    this.#sync()
  }

  /** Show the picture or the source, and draw the picture if it has changed. */
  #sync() {
    const src = this.#node.textContent
    /*
     * This view handles EVERY code block, not only the mermaid ones.
     *
     * ProseMirror has no way for a node-view factory to decline a node: what
     * it returns is the view, and returning nothing crashes the editor. So a
     * plain code block is handled here too -- as nothing but its source,
     * which is what ProseMirror would have rendered anyway.
     */
    const isDiagram = this.#node.attrs.language === 'mermaid'
    const showSource = this.#selected || !isDiagram
    this.#figure.hidden = showSource
    this.#source.hidden = !showSource

    if (!isDiagram || showSource || src === this.#drawn) return
    this.#drawn = src
    // Un-stamp, or mermaid skips an element it has already handled.
    this.#figure.removeAttribute('data-processed')
    this.#figure.textContent = src
    loadMermaid()
      .then((mermaid) => {
        if (!this.#figure.isConnected || this.#drawn !== src) return
        return mermaid.run({ nodes: [this.#figure] })
      })
      .then(() => {
        // The labels only exist once mermaid has drawn them.
        if (this.#drawn === src) {
          this.#decorate()
          this.#drawPins()
        }
      })
      .catch((err) => {
        // The library did not load, or the text is not valid mermaid. Both end
        // the same way: show what was actually written, which is a code block.
        console.warn('mermaid render failed', err)
        this.#figure.hidden = true
        this.#source.hidden = false
      })
  }

  /**
   * Tint the labels that carry comment threads.
   *
   * The same job `MermaidBlock` does for the read view, and deliberately the
   * same CONTRACT: `.anchor-label` with `data-threads`, `data-state` and
   * `data-active` on the label element. The click delegation, the highlight
   * store and the sidebar all work off those attributes, so matching them
   * means the editor gets every one of those behaviours without a second
   * implementation to keep in step.
   *
   * Called after each render and whenever the ranges or the active thread
   * change -- never by re-rendering, so hovering a thread does not redraw the
   * diagram.
   */
  setComments(ranges: BlockRange[], active: string | null, hl: Highlights | null) {
    this.#ranges = ranges
    this.#active = active
    this.#hl = hl
    this.#decorate()
    this.#drawPins()
  }

  /** A pin's accessible name: never the Mermaid source. */
  #pinLabel(r: BlockRange): string {
    const src = this.#node.textContent
    const shown = diagramShown(r.text, src)
    const words = shown && shown.length > 60 ? `${shown.slice(0, 59)}…` : shown
    return `Go to the ${r.state} comment on ${words ? `the diagram label “${words}”` : 'the diagram'}`
  }

  #drawPins() {
    /*
     * ONLY when the set of threads has actually changed.
     *
     * This is called whenever the tinting is refreshed, and that includes
     * when a thread becomes ACTIVE -- which is what happens when somebody
     * focuses a pin. Rebuilding then destroys the element that has focus, so
     * the browser moves focus away, the pin's own blur handler clears the
     * active thread, and the label it was meant to light never lights. The
     * read view avoids this by keying its {#each}; this is the same idea,
     * spelled out.
     */
    const sig = this.#ranges.map((r) => `${r.threadId}:${r.state}`).join('|')
    if (sig === this.#pinSig && this.dom.querySelector(':scope > .anchor-pin')) return
    this.#pinSig = sig
    /*
     * Appended to the diagram itself, not to a wrapper.
     *
     * The read view puts them as direct children of `.diagram`, and the
     * styling and the tests both address them that way (`.diagram >
     * button.anchor-pin`). A wrapper around them is one more thing for the
     * two views to disagree about.
     *
     * Rebuilt wholesale: there are a handful at most, and reconciling them by
     * hand is how the read view's `reconcile()` became something to delete.
     */
    for (const old of Array.from(this.dom.querySelectorAll(':scope > .anchor-pin'))) old.remove()
    for (const r of this.#ranges) {
      const pin = document.createElement('button')
      pin.type = 'button'
      pin.className = 'anchor-pin'
      pin.setAttribute('data-anchor-pin', '')
      pin.setAttribute('data-thread-id', r.threadId)
      pin.setAttribute('data-state', r.state)
      pin.setAttribute('aria-label', this.#pinLabel(r))
      pin.addEventListener('click', () => this.#hl?.activate(r.threadId))
      pin.addEventListener('focus', () => {
        if (this.#hl) this.#hl.active = r.threadId
      })
      pin.addEventListener('blur', () => {
        if (this.#hl && this.#hl.active === r.threadId) this.#hl.active = null
      })
      this.dom.appendChild(pin)
    }
  }

  #decorate() {
    const node = this.#figure
    for (const el of Array.from(node.querySelectorAll('.anchor-label'))) {
      el.classList.remove('anchor-label')
      el.removeAttribute('data-threads')
      el.removeAttribute('data-state')
      el.removeAttribute('data-active')
    }
    // On the `.mermaid` element, which is where the read view puts them and
    // where the outline CSS looks -- not on the wrapper around it.
    this.#figure.removeAttribute('data-threads')
    this.#figure.removeAttribute('data-state')
    this.#figure.removeAttribute('data-active')
    // Not drawn yet, or nothing to say about it.
    if (!node.querySelector('svg') || this.#ranges.length === 0) return

    const text = this.#node.textContent
    const labels = Array.from(node.querySelectorAll<HTMLElement>(LABEL_SELECTOR)).map((el) => ({
      el,
      key: labelKey(el.textContent),
    }))
    const onLabel = new Map<HTMLElement, BlockRange[]>()
    const onHost: BlockRange[] = []
    for (const r of this.#ranges) {
      const key = diagramKey(text.slice(r.from, r.to)).key
      // Every label drawn with these words: a label written twice is tinted twice.
      const hits = key ? labels.filter((l) => l.key === key) : []
      if (hits.length === 0) onHost.push(r)
      for (const { el } of hits) onLabel.set(el, [...(onLabel.get(el) ?? []), r])
    }
    for (const [el, rs] of onLabel) {
      const cover = coverOfThreads(rs)
      if (!cover) continue
      el.classList.add('anchor-label')
      el.setAttribute('data-threads', cover.threads.join(' '))
      el.setAttribute('data-state', cover.state)
      if (this.#active && cover.threads.includes(this.#active)) el.setAttribute('data-active', '')
    }
    // A thread whose words match no label belongs to the diagram as a whole.
    const hostCover = coverOfThreads(onHost)
    if (hostCover) {
      this.#figure.setAttribute('data-threads', hostCover.threads.join(' '))
      this.#figure.setAttribute('data-state', hostCover.state)
      if (this.#active && hostCover.threads.includes(this.#active)) {
        this.#figure.setAttribute('data-active', '')
      }
    }
  }

  update(node: PMNode): boolean {
    if (node.type !== this.#node.type) return false
    // A language change is handled, not refused: setting a code block's
    // language to mermaid should draw it without the node being rebuilt.
    this.#node = node
    this.#sync()
    return true
  }

  selectNode() {
    this.#selected = true
    this.dom.classList.add('pm-diagram-open')
    this.#sync()
  }

  deselectNode() {
    this.#selected = false
    this.dom.classList.remove('pm-diagram-open')
    this.#sync()
  }

  /**
   * The caret moved. ProseMirror only calls select/deselect for a NODE
   * selection, and a caret sitting inside the source is a text selection --
   * so the source has to stay open for that too, or typing in it would make
   * it disappear after the first keystroke.
   */
  setSelection() {
    this.#selected = true
    this.dom.classList.add('pm-diagram-open')
    this.#sync()
  }

  /** Mermaid's own DOM is not the document's. Never diff it. */
  ignoreMutation(mutation: ViewMutationRecord): boolean {
    return !this.contentDOM.contains(mutation.target)
  }

  /**
   * Events on the picture are not the editor's.
   *
   * ProseMirror maps a mouse event to a document position and moves the
   * selection there, which would collapse a drag across a diagram's labels
   * the moment it began -- and that drag is how somebody selects a label to
   * comment on it. Left to the browser, the selection behaves like a
   * selection over any other piece of rendered content.
   */
  stopEvent(event: Event): boolean {
    return !this.#figure.hidden && this.#figure.contains(event.target as Node)
  }
}
